import { setNoindex, clearNoindex } from "../lib/seo";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, optimizeImg, toEmbed } from "../lib/api";
import { ArrowLeft, ArrowRight, CalendarDays, Dna, FlaskConical, GitBranch, Sparkles, Star, Target, Users } from "lucide-react";
import { StatusPill } from "./Projects";

const ROLE_STYLES = {
  "Foundation Female": "border-qbprimary/50 text-qbprimary",
  "Foundation Male": "border-qbprimary/50 text-qbprimary",
  "Possible Sire": "border-amber-400/50 text-amber-300",
  "Confirmed Sire": "border-emerald-400/50 text-emerald-300",
  Dam: "border-qbprimary/50 text-qbprimary",
  Holdback: "border-violet-400/50 text-violet-300",
  "Future Breeder": "border-violet-400/50 text-violet-300",
  "Supporting Animal": "border-white/20 text-qbfg/60",
};

const Section = ({ icon: Icon, eyebrow, title, children, testid }) => (
  <motion.section
    data-testid={testid}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="mt-16 md:mt-24"
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && <Icon className="w-4 h-4 text-qbprimary" />}
      <span className="text-xs tracking-[0.28em] uppercase text-qbprimary font-semibold">{eyebrow}</span>
    </div>
    {title && <h2 className="font-display text-3xl md:text-4xl mb-6 leading-[1.08] pb-0.5">{title}</h2>}
    {children}
  </motion.section>
);

const Prose = ({ text }) => (
  <div className="space-y-4 max-w-3xl">
    {(text || "").split(/\n+/).filter(Boolean).map((para, i) => (
      <p key={i} className="text-qbfg/70 leading-relaxed">{para}</p>
    ))}
  </div>
);

const GeneChips = ({ morphs = [], het = [] }) => (
  <div className="flex flex-wrap gap-1.5 mt-3">
    {morphs.map((m) => <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-qbprimary/10 border border-qbprimary/30 text-qbprimary">{m}</span>)}
    {het.map((h) => <span key={h} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-qbfg/55">het {h}</span>)}
  </div>
);

const AnimalCard = ({ a, testid }) => (
  <Link to={`/boa/${a.boa.id}`} data-testid={testid}
    className="group border border-white/10 rounded-xl overflow-hidden bg-qbsurface hover:border-qbprimary/40 transition-colors flex flex-col">
    <div className="aspect-[4/3] overflow-hidden bg-qbbg">
      {a.boa.images?.[0] && <img src={optimizeImg(a.boa.images[0], 600)} alt={a.boa.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
    </div>
    <div className="p-4 md:p-5 flex flex-col flex-1">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <h3 className="font-display text-xl md:text-2xl group-hover:text-qbprimary transition-colors">{a.boa.name}</h3>
        <span className={`text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${ROLE_STYLES[a.role] || ROLE_STYLES["Supporting Animal"]}`}>{a.role}</span>
      </div>
      <p className="text-xs text-qbfg/50 mt-1">{[a.boa.sex, a.boa.year].filter(Boolean).join(" · ")}</p>
      {a.note && <p className="text-sm text-qbfg/65 mt-2.5 leading-relaxed">{a.note}</p>}
      <GeneChips morphs={a.boa.morphs} het={a.boa.het} />
      <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs text-qbprimary">View profile <ArrowRight className="w-3 h-3" /></span>
    </div>
  </Link>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/projects/${slug}`)
      .then((r) => { setProject(r.data); setState("ok"); })
      .catch(() => setState("missing"));
  }, [slug]);

  useEffect(() => {
    if (!project) return;
    const prevTitle = document.title;
    document.title = `${project.title} — Quantum Boas`;
    clearNoindex();
    const upsert = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = project.thesis || project.subtitle || "";
    upsert("name", "description", desc);
    upsert("property", "og:title", `${project.title} — Quantum Boas`);
    upsert("property", "og:description", desc);
    upsert("property", "og:image", project.hero_image);
    upsert("property", "og:type", "article");
    return () => { document.title = prevTitle; };
  }, [project]);

  useEffect(() => {
    if (state === "missing") { document.title = "Project Not Found | Quantum Boas"; setNoindex(); }
  }, [state]);

  if (state === "loading") return <div className="min-h-screen pt-40 pb-28 text-center text-qbfg/40">Loading project…</div>;
  if (state === "missing") return (
    <div className="min-h-screen pt-40 pb-28 text-center" data-testid="project-not-found">
      <p className="text-qbfg/50 mb-6">This project could not be found.</p>
      <Link to="/projects" className="text-qbprimary hover:underline">← Back to all projects</Link>
    </div>
  );

  const p = project;
  const timeline = [...(p.timeline || [])].sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"));

  return (
    <div className="min-h-screen pb-28" data-testid="project-detail">
      {/* HERO */}
      <div className="relative min-h-[52vh] md:min-h-[62vh] flex items-end overflow-hidden">
        {p.hero_image ? (
          <img src={optimizeImg(p.hero_image, 1600)} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-qbsurface" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-qbbg via-qbbg/55 to-qbbg/20" />
        <div className="relative w-full max-w-[1200px] mx-auto px-6 md:px-10 pb-10 pt-40">
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-qbfg/60 hover:text-qbprimary transition-colors mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> All projects
          </Link>
          <div className="flex flex-wrap gap-2 mb-5">
            <StatusPill status={p.status} />
            {p.generation && <span data-testid="project-generation" className="text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border border-white/15 text-qbfg/70 bg-qbbg/70 backdrop-blur">{p.generation}</span>}
            {p.animals.length > 0 && <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border border-white/15 text-qbfg/70 bg-qbbg/70 backdrop-blur"><Users className="w-3 h-3" /> {p.animals.length} animals</span>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.03] pb-1 max-w-3xl">{p.title}</h1>
          {p.subtitle && <p className="watercolor-text italic text-lg md:text-xl mt-2 max-w-2xl">{p.subtitle}</p>}
          {p.thesis && <p className="text-qbfg/75 mt-4 max-w-2xl leading-relaxed" data-testid="project-thesis">{p.thesis}</p>}
          {(p.genetics || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-5" data-testid="project-genetics-chips">
              <Dna className="w-3.5 h-3.5 text-qbprimary mt-1" />
              {p.genetics.map((g) => <span key={g} className="text-[10px] px-2.5 py-1 rounded-full bg-qbprimary/10 border border-qbprimary/30 text-qbprimary">{g}</span>)}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {p.story && (
          <Section icon={GitBranch} eyebrow="The idea" testid="section-story">
            <Prose text={p.story} />
          </Section>
        )}

        {p.animals.length > 0 && (
          <Section icon={Users} eyebrow="Foundation animals" title="The animals behind the project" testid="section-animals">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {p.animals.map((a) => <AnimalCard key={a.boa_id} a={a} testid={`foundation-animal-${a.boa.id}`} />)}
            </div>
            {p.animals.some((a) => a.role === "Possible Sire") && (
              <p className="text-xs text-qbfg/50 mt-4 italic" data-testid="paternity-note">
                Animals marked “Possible Sire” cohabitate within a breeding group — paternity is reported as possible or unknown unless confirmed.
              </p>
            )}
          </Section>
        )}

        {p.why_animals && (
          <Section icon={Sparkles} eyebrow="Why these animals?" testid="section-why">
            <Prose text={p.why_animals} />
          </Section>
        )}

        {p.genetics_text && (
          <Section icon={Dna} eyebrow="Genetic architecture" testid="section-genetics">
            <div className="border border-qbprimary/25 rounded-2xl bg-qbprimary/5 p-6 md:p-8">
              <Prose text={p.genetics_text} />
            </div>
          </Section>
        )}

        {(p.phenotype_goals || []).length > 0 && (
          <Section icon={Target} eyebrow="Phenotype goals" title="What we're selecting for" testid="section-phenotype">
            <div className="flex flex-wrap gap-2">
              {p.phenotype_goals.map((g) => (
                <span key={g} className="text-sm px-4 py-2 rounded-full border border-white/15 bg-qbsurface text-qbfg/80">{g}</span>
              ))}
            </div>
          </Section>
        )}

        {p.learning && (
          <Section icon={FlaskConical} eyebrow="What we're trying to learn" testid="section-learning">
            <div className="border-l-2 border-qbprimary/60 pl-5 md:pl-7">
              <Prose text={p.learning} />
            </div>
          </Section>
        )}

        {p.outcomes_text && (
          <Section icon={Sparkles} eyebrow="Expected / possible outcomes" testid="section-outcomes">
            <Prose text={p.outcomes_text} />
            <p className="text-xs text-qbfg/45 italic mt-4">Possibilities, not guarantees — outcomes are documented as they actually happen in the timeline below.</p>
          </Section>
        )}

        {timeline.length > 0 && (
          <Section icon={CalendarDays} eyebrow="Project timeline" title="The journal so far" testid="section-timeline">
            <div className="relative border-l border-white/10 ml-2 space-y-10">
              {timeline.map((t) => (
                <div key={t.id} className="relative pl-7 md:pl-9" data-testid={`timeline-entry-${t.id}`}>
                  <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-qbprimary shadow-[0_0_12px_2px_rgba(0,242,96,0.35)]" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-qbprimary">{t.date || "Journal update"}</span>
                  <h3 className="font-display text-xl md:text-2xl mt-1.5">{t.title}</h3>
                  {t.description && <div className="mt-2.5"><Prose text={t.description} /></div>}
                  {(t.boa_list || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {t.boa_list.map((b) => (
                        <Link key={b.id} to={`/boa/${b.id}`} data-testid={`timeline-animal-${b.id}`} className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-qbsurface text-qbfg/70 hover:border-qbprimary/50 hover:text-qbprimary transition-colors">{b.name}</Link>
                      ))}
                    </div>
                  )}
                  {(t.images || []).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 max-w-2xl">
                      {t.images.map((src, i) => (
                        <div key={i} className="aspect-square rounded-lg overflow-hidden bg-qbsurface">
                          <img src={optimizeImg(src, 500)} alt={`${t.title} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                  {t.video_url && (
                    <div className="aspect-video rounded-xl overflow-hidden bg-qbsurface mt-4 max-w-2xl">
                      <iframe src={toEmbed(t.video_url)} title={t.title || "Project video"} className="w-full h-full" loading="lazy" scrolling="no"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {p.holdbacks.length > 0 && (
          <Section icon={Star} eyebrow="Holdbacks" title="Animals we've kept back" testid="section-holdbacks">
            <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
              {p.holdbacks.map((h) => (
                <div key={h.boa_id} className="border border-violet-400/25 rounded-xl bg-violet-400/5 p-5" data-testid={`holdback-${h.boa.id}`}>
                  <Link to={`/boa/${h.boa.id}`} className="group flex items-center gap-4">
                    <span className="w-16 h-16 rounded-lg overflow-hidden bg-qbbg shrink-0">
                      {h.boa.images?.[0] && <img src={optimizeImg(h.boa.images[0], 300)} alt={h.boa.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    </span>
                    <span>
                      <span className="font-display text-xl group-hover:text-qbprimary transition-colors block">{h.boa.name}</span>
                      <span className="text-xs text-qbfg/50">{[h.boa.sex, ...(h.boa.morphs || [])].filter(Boolean).join(" · ")}</span>
                    </span>
                  </Link>
                  {h.reason && <p className="text-sm text-qbfg/70 mt-3"><span className="text-qbfg/45 text-xs uppercase tracking-wider block mb-1">Why it was selected</span>{h.reason}</p>}
                  {h.observations && <p className="text-sm text-qbfg/70 mt-2"><span className="text-qbfg/45 text-xs uppercase tracking-wider block mb-1">Phenotype observations</span>{h.observations}</p>}
                  {h.future_role && <p className="text-sm text-qbfg/70 mt-2"><span className="text-qbfg/45 text-xs uppercase tracking-wider block mb-1">Future role</span>{h.future_role}</p>}
                  {(h.images || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {h.images.map((src, i) => (
                        <div key={i} className="aspect-square rounded-lg overflow-hidden bg-qbbg">
                          <img src={optimizeImg(src, 400)} alt={`${h.boa.name} progression ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {p.next_generation && (
          <Section icon={ArrowRight} eyebrow="Next generation" title="Where this project goes next" testid="section-next-gen">
            <div className="border border-white/10 rounded-2xl bg-qbsurface p-6 md:p-8">
              <Prose text={p.next_generation} />
            </div>
          </Section>
        )}

        {p.north_star && (
          <Section icon={Star} eyebrow="Long-term north star" testid="section-north-star">
            <div className="border border-qbprimary/35 rounded-2xl bg-gradient-to-br from-qbprimary/10 via-transparent to-transparent p-6 md:p-8">
              <Prose text={p.north_star} />
            </div>
          </Section>
        )}

        {p.connections.length > 0 && (
          <Section icon={GitBranch} eyebrow="Connected projects" title="Part of a bigger picture" testid="section-connections">
            <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
              {p.connections.map((c) => (
                <Link key={c.project_id} to={`/projects/${c.project.slug}`} data-testid={`connection-${c.project.slug}`}
                  className="group border border-white/10 rounded-xl overflow-hidden bg-qbsurface hover:border-qbprimary/40 transition-colors">
                  {c.project.hero_image && (
                    <div className="aspect-[16/7] overflow-hidden bg-qbbg">
                      <img src={optimizeImg(c.project.hero_image, 800)} alt={c.project.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-5">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-qbfg/45">{c.project.status}</span>
                    <h3 className="font-display text-xl md:text-2xl mt-1 group-hover:text-qbprimary transition-colors">{c.project.title}</h3>
                    {c.note && <p className="text-sm text-qbfg/65 mt-2 leading-relaxed">{c.note}</p>}
                    <span className="inline-flex items-center gap-1.5 text-xs text-qbprimary mt-3">Open journal <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        <p className="font-display italic text-lg text-qbfg/60 mt-20 md:mt-28 border-l-2 border-qbprimary/60 pl-4">
          “Genetics tell us what is possible. Selection determines where we take it.”
        </p>
      </div>
    </div>
  );
}
